import { uploadImage as uploader } from "@/http/api";
import { User } from "@/http/api-types";
import { cn } from "@/lib/utils";
import { editorViewCtx, editorViewOptionsCtx } from "@milkdown/core";
import { Crepe, CrepeFeature } from "@milkdown/crepe";
import "@milkdown/crepe/theme/common/style.css";
import "@milkdown/crepe/theme/frame.css";
import { Milkdown, MilkdownProvider, useEditor, useInstance } from "@milkdown/react";
import { $view, getMarkdown, replaceAll } from "@milkdown/utils";
import type { Node as ProseMirrorNode } from "prosemirror-model";
import { TextSelection } from "prosemirror-state";
import type { EditorView } from "prosemirror-view";
import { useCallback, useEffect, useRef } from "react";
import { toast } from "sonner";
import { EditorMentionAutocomplete } from "./editor-mention-autocomplete";
import { mentionNode } from "./editor-mention-node";
import { MentionNodeView } from "./editor-mention-nodeview";
import { EditorToolbar } from "./editor-toolbar";

interface EditorProps {
  value: string;
  onChange?: (value: string) => void;
  readOnly?: boolean;
  placeholder?: string;
  className?: string;
  editorClassName?: string;
  experimentId?: string;
  onPendingImageUploadsChange?: (pendingUploads: number) => void;
}

function EditorValueSync({ value, readOnly }: { value: string; readOnly: boolean }) {
  const [isLoading, getInstance] = useInstance();

  useEffect(() => {
    if (isLoading) return;

    const editor = getInstance();
    if (!editor) return;

    const currentMarkdown = editor.action(getMarkdown());

    if (readOnly && currentMarkdown !== value) {
      editor.action(replaceAll(value));
      return;
    }

    if (!readOnly && !currentMarkdown && value && currentMarkdown !== value) {
      editor.action(replaceAll(value));
    }
  }, [value, isLoading, getInstance, readOnly]);

  return null;
}

async function uploadImage(file: File, experimentId: string): Promise<string> {
  if (!experimentId) {
    throw new Error("Experiment ID is required for image upload");
  }

  const formData = new FormData();
  formData.append("image", file);

  try {
    const { url } = await uploader(formData, experimentId);

    if (!url) {
      toast.error("Image upload failed: no URL returned");
    }

    return url;
  } catch (error) {
    console.error("Image upload failed:", error);
    toast.error("Image upload failed");
    throw error;
  }
}

function imageFilesFromDataTransfer(dataTransfer: DataTransfer | null | undefined): File[] {
  if (!dataTransfer) {
    return [];
  }

  const files = Array.from(dataTransfer.files ?? []);
  const itemFiles = Array.from(dataTransfer.items ?? [])
    .filter(item => item.kind === "file")
    .map(item => item.getAsFile())
    .filter((file): file is File => Boolean(file));

  const seenFiles = new Set<string>();
  return [...files, ...itemFiles].filter(file => {
    if (!file.type.startsWith("image/")) {
      return false;
    }

    const fileKey = `${file.name}:${file.type}:${file.size}:${file.lastModified}`;
    if (seenFiles.has(fileKey)) {
      return false;
    }

    seenFiles.add(fileKey);
    return true;
  });
}

function imageNodeFromUpload(file: File, url: string, view: EditorView): ProseMirrorNode | null {
  const image = view.state.schema.nodes["image"];
  if (!image) {
    return null;
  }

  return image.createAndFill({ src: url, alt: file.name }) ?? image.create({ src: url, alt: file.name });
}

function insertImageNodes(view: EditorView, nodes: ProseMirrorNode[], insertPos: number) {
  if (nodes.length === 0) {
    return;
  }

  let tr = view.state.tr;
  let pos = Math.min(insertPos, tr.doc.content.size);

  for (const node of nodes) {
    tr = tr.insert(pos, node);
    pos += node.nodeSize;
  }

  view.dispatch(tr.scrollIntoView());
}

function CrepeEditor({
  value,
  readOnly,
  placeholder,
  onChange,
  experimentId,
  onPendingImageUploadsChange,
}: Pick<
  EditorProps,
  "value" | "readOnly" | "placeholder" | "onChange" | "experimentId" | "onPendingImageUploadsChange"
>) {
  const pendingImageUploadsRef = useRef(0);

  const updatePendingImageUploads = useCallback(
    (delta: number) => {
      const nextPendingUploads = Math.max(0, pendingImageUploadsRef.current + delta);
      pendingImageUploadsRef.current = nextPendingUploads;
      onPendingImageUploadsChange?.(nextPendingUploads);
    },
    [onPendingImageUploadsChange],
  );

  const trackedUploadImage = useCallback(
    async (file: File) => {
      if (!experimentId) {
        throw new Error("Cannot upload image: no experiment ID");
      }

      updatePendingImageUploads(1);
      try {
        return await uploadImage(file, experimentId);
      } finally {
        updatePendingImageUploads(-1);
      }
    },
    [experimentId, updatePendingImageUploads],
  );

  useEffect(() => {
    return () => {
      pendingImageUploadsRef.current = 0;
      onPendingImageUploadsChange?.(0);
    };
  }, [onPendingImageUploadsChange]);

  useEditor(root => {
    const handleImageFileInsert = (view: EditorView, dataTransfer: DataTransfer | null | undefined) => {
      const imageFiles = imageFilesFromDataTransfer(dataTransfer);
      if (!experimentId || imageFiles.length === 0) {
        return false;
      }

      const insertPos = view.state.selection.from;
      Promise.all(
        imageFiles.map(async file => {
          const url = await trackedUploadImage(file);
          return imageNodeFromUpload(file, url, view);
        }),
      )
        .then(nodes => {
          insertImageNodes(
            view,
            nodes.filter((node): node is ProseMirrorNode => Boolean(node)),
            insertPos,
          );
        })
        .catch(() => {
          // uploadImage already reports the failure to the user.
        });

      return true;
    };

    // Configure Crepe features
    const features: Partial<Record<CrepeFeature, boolean>> = {
      [CrepeFeature.CodeMirror]: true,
      [CrepeFeature.ListItem]: true,
      [CrepeFeature.BlockEdit]: true,
      [CrepeFeature.Cursor]: true,
      [CrepeFeature.LinkTooltip]: true,
      [CrepeFeature.Toolbar]: !readOnly,
      [CrepeFeature.Placeholder]: true,
      [CrepeFeature.Table]: true,
      [CrepeFeature.Latex]: false,
      [CrepeFeature.ImageBlock]: !readOnly && !!experimentId, // Only enable if editable and has experimentId
    };

    const crepe = new Crepe({
      root,
      defaultValue: value || "",
      features,
      featureConfigs: {
        [CrepeFeature.Placeholder]: {
          text: placeholder || "Type '/' for commands",
        },
        [CrepeFeature.ImageBlock]: {
          onUpload: async (file: File) => {
            if (!experimentId) {
              throw new Error("Cannot upload image: no experiment ID");
            }
            return trackedUploadImage(file);
          },
        },
      },
    });

    if (!readOnly) {
      crepe.editor.config(ctx => {
        ctx.update(editorViewOptionsCtx, previousOptions => ({
          ...previousOptions,
          handlePaste: (view, event, slice) => {
            if (handleImageFileInsert(view, event.clipboardData)) {
              event.preventDefault();
              return true;
            }

            return previousOptions.handlePaste?.(view, event, slice) ?? false;
          },
          handleDrop: (view, event, slice, moved) => {
            if (handleImageFileInsert(view, event.dataTransfer)) {
              event.preventDefault();
              return true;
            }

            return previousOptions.handleDrop?.(view, event, slice, moved) ?? false;
          },
        }));
      });
    }

    if (!readOnly && onChange) {
      crepe.on(listenerManager => {
        listenerManager.markdownUpdated((ctx, markdown) => {
          onChange(markdown);
        });
      });
    }

    if (readOnly) {
      crepe.setReadonly(true);
    }

    // Register custom mention node + React NodeView
    crepe.editor.use(mentionNode);
    crepe.editor.use($view(mentionNode, () => node => new MentionNodeView(node)));

    return crepe;
  });

  return <Milkdown />;
}

// MentionInserter lives inside MilkdownProvider so it can access the editor instance.
// It exposes an insert function via a ref so the outer Editor component can call it.
function MentionInserter({ insertRef }: { insertRef: React.MutableRefObject<((user: User) => void) | null> }) {
  const [, getInstance] = useInstance();

  insertRef.current = (user: User) => {
    const editor = getInstance();
    if (!editor) return;

    editor.action(ctx => {
      const view = ctx.get(editorViewCtx);
      const { state, dispatch } = view;
      const { selection, schema } = state;
      const { from } = selection;

      // Find the @query before caret in the ProseMirror document
      const textBefore = state.doc.textBetween(Math.max(0, from - 100), from);
      const atIndex = textBefore.lastIndexOf("@");
      if (atIndex === -1) return;

      const deleteFrom = from - (textBefore.length - atIndex);
      const mentionType = schema.nodes["mention"];
      if (!mentionType) return;
      const mentionPMNode = mentionType.create({ userId: user.id });
      const space = schema.text(" ");

      const insertEnd = deleteFrom + mentionPMNode.nodeSize + 1;
      const tr = state.tr
        .delete(deleteFrom, from)
        .insert(deleteFrom, mentionPMNode)
        .insert(deleteFrom + mentionPMNode.nodeSize, space);
      tr.setSelection(TextSelection.create(tr.doc, insertEnd));

      dispatch(tr);
      view.focus();
    });
  };

  return null;
}

export function Editor({
  value,
  onChange,
  readOnly = false,
  placeholder,
  className,
  editorClassName,
  experimentId,
  onPendingImageUploadsChange,
}: EditorProps) {
  const editorContainerRef = useRef<HTMLDivElement>(null);
  const insertMentionRef = useRef<((user: User) => void) | null>(null);

  function insertStructuredMention(user: User) {
    insertMentionRef.current?.(user);
  }

  function handleMentionClick() {
    const contentEditable = editorContainerRef.current?.querySelector<HTMLElement>('[contenteditable="true"]');
    if (!contentEditable) return;
    contentEditable.focus();
    document.execCommand("insertText", false, "@");
  }

  return (
    <div className={`editor-container ${className || ""}`}>
      <div
        ref={editorContainerRef}
        className={cn(
          "editor relative min-h-[220px] overflow-visible rounded-md bg-background text-sm",
          readOnly ? "editor-readonly" : "border border-input",
          editorClassName,
        )}
      >
        <MilkdownProvider key={readOnly ? "readonly" : "editable"}>
          {!readOnly && <EditorToolbar experimentId={experimentId} onMentionClick={handleMentionClick} />}
          <CrepeEditor
            value={value}
            readOnly={readOnly}
            placeholder={placeholder}
            onChange={onChange}
            experimentId={experimentId}
            onPendingImageUploadsChange={onPendingImageUploadsChange}
          />
          <EditorValueSync value={value} readOnly={readOnly} />
          {!readOnly && <MentionInserter insertRef={insertMentionRef} />}
        </MilkdownProvider>
      </div>
      {!readOnly && <EditorMentionAutocomplete editorRef={editorContainerRef} onInsert={insertStructuredMention} />}
    </div>
  );
}
